import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Row, Col, Card, Form, Input, Button, notification, Space, Typography,
  DatePicker, Upload, Progress, Tag, Alert, Divider, Tabs,
} from 'antd';
import {
  SaveOutlined, ArrowLeftOutlined, UploadOutlined, FilePdfOutlined,
  CheckCircleOutlined, InfoCircleOutlined, DeleteOutlined, FileImageOutlined,
  PlusOutlined, MinusCircleOutlined, BulbOutlined, FileTextOutlined,
  OrderedListOutlined, CalendarOutlined,
} from '@ant-design/icons';
import { Select } from 'antd';
import moment from 'moment';
import { createReport, readReport, updateReport, uploadReportFile, deleteReportFile } from './monthlyReportService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

// ── Parse description field — may be JSON or plain text ──────────────────────
function parseDescription(raw) {
  if (!raw) return { text: '', points: [], expenditure: [] };
  try {
    const parsed = JSON.parse(raw);
    return {
      text:        parsed.text        || '',
      points:      Array.isArray(parsed.points)      ? parsed.points      : [],
      expenditure: Array.isArray(parsed.expenditure) ? parsed.expenditure : [],
    };
  } catch {
    return { text: raw, points: [], expenditure: [] };
  }
}

function serializeDescription(text, points, expenditure) {
  const filteredPoints = points.filter(p => p.trim());
  const filteredExp    = expenditure.filter(e => e.label?.trim() || e.amount);
  if (!text && !filteredPoints.length && !filteredExp.length) return '';
  return JSON.stringify({ text, points: filteredPoints, expenditure: filteredExp });
}

// ── Dynamic bullet list builder ───────────────────────────────────────────────
function BulletListBuilder({ items, onChange, placeholder = 'बिंदु लिखें...' }) {
  const add    = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, val) => onChange(items.map((v, idx) => idx === i ? val : v));

  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
          <span style={{ marginTop: 8, color: '#7a0000', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>•</span>
          <Input
            value={item}
            onChange={e => update(i, e.target.value)}
            placeholder={placeholder}
            style={{ flex: 1 }}
            onPressEnter={() => { if (i === items.length - 1) add(); }}
          />
          <Button
            type="text" danger icon={<MinusCircleOutlined />}
            onClick={() => remove(i)}
            style={{ marginTop: 2, flexShrink: 0 }}
          />
        </div>
      ))}
      <Button
        type="dashed" icon={<PlusOutlined />} onClick={add}
        style={{ width: '100%', marginTop: 4, borderColor: '#7a0000', color: '#7a0000' }}
      >
        बिंदु जोड़ें
      </Button>
    </div>
  );
}

// ── Expenditure row builder ───────────────────────────────────────────────────
function ExpenditureBuilder({ items, onChange }) {
  const emptyRow = () => ({ label: '', amount: '', note: '' });
  const add    = () => onChange([...items, emptyRow()]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, field, val) => onChange(items.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  return (
    <div>
      {items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr 32px', gap: 6, marginBottom: 6 }}>
          <Text style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>विवरण (Item)</Text>
          <Text style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>राशि (₹)</Text>
          <Text style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>नोट</Text>
          <span />
        </div>
      )}
      {items.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr 32px', gap: 6, marginBottom: 8 }}>
          <Input value={row.label}  onChange={e => update(i, 'label',  e.target.value)} placeholder="जैसे: भवन निर्माण, यात्रा..." />
          <Input value={row.amount} onChange={e => update(i, 'amount', e.target.value)} placeholder="0" prefix="₹" type="number" />
          <Input value={row.note}   onChange={e => update(i, 'note',   e.target.value)} placeholder="वैकल्पिक नोट..." />
          <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(i)} />
        </div>
      ))}
      <Button
        type="dashed" icon={<PlusOutlined />} onClick={add}
        style={{ width: '100%', marginTop: 4, borderColor: '#52c41a', color: '#52c41a' }}
      >
        व्यय मद जोड़ें
      </Button>
    </div>
  );
}

export default function MonthlyReportForm() {
  const history  = useHistory();
  const { id }   = useParams();
  const isEdit   = Boolean(id);

  const [form]             = Form.useForm();
  const [saving, setSaving]             = useState(false);
  const [loading, setLoading]           = useState(false);
  const [pdfFile, setPdfFile]           = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading]       = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [currentFileUrl, setCurrentFileUrl] = useState(null);
  const [reportId, setReportId]         = useState(id || null);

  // Period type: 'monthly' | 'yearly' | 'custom'
  const [periodType, setPeriodType] = useState('monthly');

  // Notes state — separate from form fields
  const [notesText,    setNotesText]    = useState('');
  const [notePoints,   setNotePoints]   = useState([]);
  const [expenditure,  setExpenditure]  = useState([]);

  useEffect(() => {
    if (isEdit) loadReport();
  }, [id]); // eslint-disable-line

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await readReport(id);
      if (res.status) {
        const r = res.result;
        // Detect period type from stored value
        const rm = r.report_month || '';
        let detectedType = 'monthly';
        let parsedMonth  = null;
        if (/^\d{4}$/.test(rm)) {
          detectedType = 'yearly';
          parsedMonth  = moment(rm, 'YYYY');
        } else if (rm.includes(' to ') || rm.includes('–')) {
          detectedType = 'custom';
        } else if (rm) {
          parsedMonth = moment(rm, 'YYYY-MM');
        }
        setPeriodType(detectedType);

        form.setFieldsValue({
          title:           r.title,
          report_month:    parsedMonth,
          report_period_custom: detectedType === 'custom' ? rm : '',
          template_name:   r.template_name,
          template_params: r.template_params
            ? (Array.isArray(r.template_params) ? r.template_params.join(', ') : r.template_params)
            : '{name}',
        });
        const parsed = parseDescription(r.description);
        setNotesText(parsed.text);
        setNotePoints(parsed.points.length ? parsed.points : []);
        setExpenditure(parsed.expenditure.length ? parsed.expenditure : []);
        setCurrentFileUrl(r.file_url);
        setReportId(r.id);
      }
    } catch (err) {
      notification.error({ message: 'Failed to load report', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const paramsRaw = values.template_params || '{name}';
      const paramsArr = paramsRaw.split(',').map(s => s.trim()).filter(Boolean);

      let reportPeriod;
      if (periodType === 'yearly') {
        reportPeriod = values.report_month?.format('YYYY');
      } else if (periodType === 'custom') {
        reportPeriod = values.report_period_custom || '';
      } else {
        reportPeriod = values.report_month?.format('YYYY-MM');
      }

      const payload = {
        title:           values.title,
        report_month:    reportPeriod,
        template_name:   values.template_name,
        template_params: paramsArr,
        description:     serializeDescription(notesText, notePoints, expenditure),
      };

      let savedId = reportId;
      if (isEdit) {
        await updateReport(id, payload);
        notification.success({ message: 'Report updated' });
      } else {
        const res = await createReport(payload);
        if (!res.status) throw new Error(res.message);
        savedId = res.id;
        setReportId(savedId);
        notification.success({ message: 'Report created' });
      }

      if (pdfFile && savedId) {
        await handleUploadPdf(savedId);
      } else if (!pdfFile) {
        history.push('/monthly-reports');
      }
    } catch (err) {
      notification.error({ message: 'Save failed', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFile = async () => {
    const useId = reportId || id;
    if (!useId) return;
    setDeletingFile(true);
    try {
      const res = await deleteReportFile(useId);
      if (res.status) {
        setCurrentFileUrl(null); setPdfFile(null); setFilePreviewUrl(null);
        notification.success({ message: 'File deleted' });
      } else throw new Error(res.message);
    } catch (err) {
      notification.error({ message: 'Delete failed', description: err.message });
    } finally { setDeletingFile(false); }
  };

  const handleUploadPdf = async (overrideId) => {
    const useId = overrideId || reportId;
    if (!pdfFile || !useId) return;
    setUploading(true); setUploadProgress(0);
    try {
      const res = await uploadReportFile(useId, pdfFile, (pct) => setUploadProgress(pct));
      if (res.status) {
        setCurrentFileUrl(res.file_url); setPdfFile(null); setFilePreviewUrl(null);
        setUploadProgress(100);
        notification.success({ message: 'File uploaded successfully' });
        setTimeout(() => { setUploadProgress(0); history.push('/monthly-reports'); }, 800);
      } else throw new Error(res.message);
    } catch (err) {
      notification.error({ message: 'Upload failed', description: err.message });
    } finally { setUploading(false); }
  };

  const totalExpenditure = expenditure.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div className="layout-content">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/monthly-reports')} />
                <Title level={4} style={{ margin: 0, color: '#fff' }}>
                  {isEdit ? 'Edit' : 'New'}{' '}
                  {periodType === 'yearly' ? 'Yearly' : periodType === 'custom' ? '' : 'Monthly'} Report
                  {periodType === 'yearly' && <Tag color="green" style={{ marginLeft: 8, fontSize: 11, verticalAlign: 'middle' }}>वार्षिक</Tag>}
                  {periodType === 'custom' && <Tag color="orange" style={{ marginLeft: 8, fontSize: 11, verticalAlign: 'middle' }}>Custom Period</Tag>}
                </Title>
              </Space>
            }
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            loading={loading}
          >
            <Row gutter={[24, 0]}>
              {/* ── Left: Form + Notes ── */}
              <Col xs={24} lg={14}>
                <Form form={form} layout="vertical" onFinish={handleSave}
                  initialValues={{ template_params: '{name}, जनवरी, समाज शिक्षा निधि समर्पण योजना' }}>
                  <Row gutter={[16, 0]}>
                    <Col xs={24}>
                      <Form.Item name="title" label="Report Title" rules={[{ required: true }]}>
                        <Input placeholder='e.g. SSNSY Maasik Prativedan – Sep-Oct 2025' maxLength={255} />
                      </Form.Item>
                    </Col>
                    {/* Period Type selector */}
                    <Col xs={24} sm={8}>
                      <Form.Item label="Report Period Type">
                        <Select
                          value={periodType}
                          onChange={(v) => { setPeriodType(v); form.setFieldsValue({ report_month: null, report_period_custom: '' }); }}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value="monthly">
                            <CalendarOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                            Monthly (मासिक)
                          </Select.Option>
                          <Select.Option value="yearly">
                            <CalendarOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                            Yearly (वार्षिक)
                          </Select.Option>
                          <Select.Option value="custom">
                            <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                            Custom Range
                          </Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Dynamic period picker */}
                    <Col xs={24} sm={16}>
                      {periodType === 'monthly' && (
                        <Form.Item name="report_month" label="Report Month" rules={[{ required: true, message: 'Month चुनें' }]}>
                          <DatePicker picker="month" style={{ width: '100%' }} format="MMM YYYY"
                            placeholder="माह चुनें" />
                        </Form.Item>
                      )}
                      {periodType === 'yearly' && (
                        <Form.Item name="report_month" label="Report Year" rules={[{ required: true, message: 'Year चुनें' }]}>
                          <DatePicker picker="year" style={{ width: '100%' }} format="YYYY"
                            placeholder="वर्ष चुनें" />
                        </Form.Item>
                      )}
                      {periodType === 'custom' && (
                        <Form.Item name="report_period_custom" label="Period (e.g. Apr 2025 – Mar 2026)"
                          rules={[{ required: true, message: 'Period लिखें' }]}>
                          <Input placeholder="जैसे: Apr 2025 – Mar 2026 या Q1 2026" />
                        </Form.Item>
                      )}
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="template_name" label="WhatsApp Template Name" rules={[{ required: true }]}
                        tooltip="AiSensy campaign/template name">
                        <Input placeholder='e.g. monthly_report_2025' />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item name="template_params" label="Template Parameters (comma-separated)"
                        extra={<span style={{ fontSize: 11 }}>Param 1: {'{name}'} auto-fills member name</span>}>
                        <Input placeholder='{name}, सितम्बर-अक्टूबर 2025, समाज शिक्षा निधि समर्पण योजना' />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* ── Notes / Expenditure Tabs ── */}
                  <Divider style={{ margin: '8px 0 16px' }}>
                    <Text style={{ color: '#7a0000', fontWeight: 700, fontSize: 13 }}>
                      📝 Notes &amp; Expenditure (दिखेगा User Portal में)
                    </Text>
                  </Divider>

                  <Tabs defaultActiveKey="notes" size="small"
                    tabBarStyle={{ marginBottom: 12 }}>
                    <TabPane tab={<span><FileTextOutlined /> सामान्य नोट्स</span>} key="notes">
                      <TextArea
                        value={notesText}
                        onChange={e => setNotesText(e.target.value)}
                        rows={4}
                        placeholder={
                          periodType === 'yearly'
                            ? 'इस वर्ष की गतिविधि, उपलब्धि, जानकारी यहाँ लिखें...'
                            : 'इस अवधि की गतिविधि, उपलब्धि, जानकारी यहाँ लिखें...'
                        }
                        maxLength={2000}
                        showCount
                      />
                    </TabPane>

                    <TabPane tab={<span><OrderedListOutlined /> मुख्य बिंदु</span>} key="points">
                      <Alert type="info" showIcon style={{ marginBottom: 10, fontSize: 12 }}
                        message="ये बुलेट पॉइंट User Portal में • list के रूप में दिखेंगे।" />
                      <BulletListBuilder
                        items={notePoints}
                        onChange={setNotePoints}
                        placeholder="जैसे: मंदिर निर्माण कार्य प्रारंभ हुआ..."
                      />
                    </TabPane>

                    <TabPane
                      tab={
                        <span>
                          <BulbOutlined /> व्यय विवरण
                          {totalExpenditure > 0 && (
                            <Tag color="green" style={{ marginLeft: 6, fontSize: 10 }}>
                              ₹ {totalExpenditure.toLocaleString('en-IN')}
                            </Tag>
                          )}
                        </span>
                      }
                      key="expenditure"
                    >
                      <Alert type="info" showIcon style={{ marginBottom: 10, fontSize: 12 }}
                        message="व्यय मदें User Portal में तालिका के रूप में दिखेंगी।" />
                      <ExpenditureBuilder items={expenditure} onChange={setExpenditure} />
                      {totalExpenditure > 0 && (
                        <div style={{ textAlign: 'right', marginTop: 10, fontWeight: 700,
                          fontSize: 14, color: '#7a0000', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                          कुल व्यय: ₹ {totalExpenditure.toLocaleString('en-IN')}
                        </div>
                      )}
                    </TabPane>
                  </Tabs>

                  <Form.Item style={{ marginTop: 16 }}>
                    <Space>
                      <Button type="primary" htmlType="submit" icon={<SaveOutlined />}
                        loading={saving || uploading}
                        style={{ background: '#7a0000', borderColor: '#7a0000' }}>
                        {pdfFile ? 'Save & Upload File' : 'Save Report'}
                      </Button>
                      <Button onClick={() => history.push('/monthly-reports')}>Cancel</Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Col>

              {/* ── Right: File Upload ── */}
              <Col xs={24} lg={10}>
                <div style={{ borderLeft: '1px solid #f0f0f0', paddingLeft: 20 }}>
                  <Title level={5} style={{ marginBottom: 12 }}>
                    <UploadOutlined style={{ color: '#7a0000', marginRight: 6 }} />
                    Upload Monthly Report File
                  </Title>

                  {currentFileUrl && (
                    <Alert type="success" icon={<CheckCircleOutlined />} showIcon
                      style={{ marginBottom: 12, fontSize: 12 }}
                      message="File uploaded"
                      description={
                        <Space>
                          <a href={currentFileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                            View current file
                          </a>
                          <Button danger size="small" icon={<DeleteOutlined />}
                            loading={deletingFile} onClick={handleDeleteFile} style={{ fontSize: 12 }}>
                            Delete File
                          </Button>
                        </Space>
                      }
                    />
                  )}

                  <Upload
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                    beforeUpload={(file) => {
                      setPdfFile(file);
                      if (file.type.startsWith('image/')) setFilePreviewUrl(URL.createObjectURL(file));
                      else setFilePreviewUrl(null);
                      return false;
                    }}
                    onRemove={() => { setPdfFile(null); setFilePreviewUrl(null); }}
                    fileList={pdfFile ? [{ uid: '1', name: pdfFile.name, status: 'done' }] : []}
                    maxCount={1}
                  >
                    <Button icon={<UploadOutlined />} size="large" style={{ width: '100%' }}>
                      {currentFileUrl ? 'Replace File (PDF / Image)' : 'Select File (PDF / Image)'}
                    </Button>
                  </Upload>

                  {pdfFile && (
                    <div style={{ marginTop: 10, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: '10px 14px' }}>
                      <Text style={{ fontSize: 12 }}>
                        {pdfFile.type.startsWith('image/')
                          ? <FileImageOutlined style={{ color: '#1890ff', marginRight: 6 }} />
                          : <FilePdfOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />}
                        <b>{pdfFile.name}</b><br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Size: {(pdfFile.size / (1024 * 1024)).toFixed(1)} MB
                          {pdfFile.size > 20 * 1024 * 1024 && (
                            <Tag color="warning" style={{ marginLeft: 8, fontSize: 11 }}>Large file</Tag>
                          )}
                        </Text>
                      </Text>
                      {filePreviewUrl && (
                        <img src={filePreviewUrl} alt="preview"
                          style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4, objectFit: 'contain', border: '1px solid #eee', marginTop: 8 }} />
                      )}
                    </div>
                  )}

                  {uploading && (
                    <div style={{ marginTop: 12 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Uploading...</Text>
                      <Progress percent={uploadProgress} size="small" style={{ marginTop: 4 }} />
                    </div>
                  )}

                  {isEdit && pdfFile && !saving && (
                    <Button type="primary" icon={<UploadOutlined />} loading={uploading}
                      onClick={() => handleUploadPdf()}
                      style={{ marginTop: 12, width: '100%', background: '#52c41a', borderColor: '#52c41a' }}>
                      Upload File Only
                    </Button>
                  )}

                  <Alert type="info" icon={<InfoCircleOutlined />} showIcon
                    style={{ marginTop: 16, fontSize: 12 }}
                    description={
                      <ul style={{ paddingLeft: 16, margin: 0, fontSize: 12 }}>
                        <li>Max file size: <b>150 MB</b></li>
                        <li>Accepted: <b>PDF, JPG, PNG, GIF, WebP</b></li>
                        <li>File sent as WhatsApp message to members</li>
                        <li>Notes / Bullet Points / Expenditure visible in <b>User Portal → Development tab</b></li>
                      </ul>
                    }
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
